import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ComicCard from "@/components/feature/ComicCard";
import useSeo from "@/hooks/useSeo";
import { comics, genres } from "@/mocks/comics";

type SortKey = "default" | "score" | "hot" | "latest";

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "default", label: "综合排序" },
  { key: "score", label: "评分优先" },
  { key: "hot", label: "热度最高" },
  { key: "latest", label: "最近更新" },
];

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const genreId = searchParams.get("genre") ?? "";
  const q = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(q);
  const [sort, setSort] = useState<SortKey>("default");

  const currentGenre = genres.find((g) => g.id === genreId);

  const filtered = useMemo(() => {
    let list = comics.filter((c) => {
      const matchGenre = genreId ? c.genre === currentGenre?.name : true;
      const kw = q.trim().toLowerCase();
      const matchKw = kw
        ? c.title.toLowerCase().includes(kw) || c.tags.some((t) => t.toLowerCase().includes(kw)) || c.intro.toLowerCase().includes(kw)
        : true;
      return matchGenre && matchKw;
    });
    if (sort === "score") list = [...list].sort((a, b) => b.score - a.score);
    if (sort === "hot") list = [...list].sort((a, b) => b.views.localeCompare(a.views));
    return list;
  }, [genreId, q, sort, currentGenre]);

  const selectGenre = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set("genre", id);
    else next.delete("genre");
    setSearchParams(next);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (keyword.trim()) next.set("q", keyword.trim());
    else next.delete("q");
    setSearchParams(next);
  };

  useSeo({
    title: currentGenre
      ? `羞羞动漫漫画${currentGenre.name}题材专区 - ${currentGenre.name}漫画大全`
      : "羞羞动漫漫画分类专区 - 恋爱、后宫、暧昧、同居、校园、逆后宫漫画",
    description: currentGenre
      ? `${currentGenre.desc}。在羞羞动漫漫画${currentGenre.name}专区浏览${currentGenre.count}部${currentGenre.name}题材漫画，分类清晰，更新及时。`
      : "浏览羞羞动漫漫画全部题材分类：恋爱、后宫、暧昧、同居、校园、逆后宫，分类清晰，更新及时，追更不迷路。",
    keywords: "羞羞动漫漫画," + genres.map((g) => `羞羞动漫漫画${g.name}`).join(","),
  });

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <div className="pt-24 md:pt-32 pb-6 max-w-7xl mx-auto px-4 md:px-8">
          <nav aria-label="面包屑" className="flex items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="hover:text-primary-600">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">{currentGenre ? `${currentGenre.name}漫画` : "分类专区"}</span>
          </nav>

          <div className="mt-6 flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-primary-600 tracking-widest">BROWSE</p>
              <h1 className="mt-2 font-heading font-bold text-2xl md:text-4xl text-foreground-950">
                {currentGenre ? `${currentGenre.name}题材漫画` : "全题材漫画分类"}
              </h1>
              <p className="mt-2 text-sm md:text-base text-foreground-600">
                {currentGenre ? currentGenre.desc : "恋爱、后宫、暧昧、同居、校园、逆后宫，六大题材分类，追更不迷路。"}
              </p>
            </div>
            <form onSubmit={handleSearch} className="flex items-center gap-2 rounded-full border border-background-300 px-4 py-2.5 bg-background-100 focus-within:border-primary-300 w-full md:w-72">
              <i className="ri-search-line text-foreground-500"></i>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索漫画"
                className="w-full bg-transparent text-sm outline-none"
              />
              <button type="submit" className="whitespace-nowrap text-sm text-primary-600 font-medium">搜索</button>
            </form>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => selectGenre("")}
              className={`px-4 py-1.5 rounded-full text-sm border whitespace-nowrap transition-colors ${!genreId ? "bg-primary-500 text-background-50 border-primary-500" : "bg-background-100 border-background-300 text-foreground-700 hover:border-primary-300"}`}
            >
              全部
            </button>
            {genres.map((g) => (
              <button
                key={g.id}
                type="button"
                onClick={() => selectGenre(g.id)}
                className={`px-4 py-1.5 rounded-full text-sm border whitespace-nowrap transition-colors ${genreId === g.id ? "bg-primary-500 text-background-50 border-primary-500" : "bg-background-100 border-background-300 text-foreground-700 hover:border-primary-300"}`}
              >
                {g.name}
              </button>
            ))}
          </div>

          <div className="mt-4 flex items-center justify-between gap-3">
            <p className="text-sm text-foreground-500">
              共 <span className="font-semibold text-primary-600">{filtered.length}</span> 部作品
            </p>
            <div className="relative">
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value as SortKey)}
                className="appearance-none pr-8 pl-4 py-2 rounded-full bg-background-100 border border-background-300 text-sm text-foreground-800 focus:border-primary-300 outline-none cursor-pointer"
              >
                {sortOptions.map((o) => (
                  <option key={o.key} value={o.key}>{o.label}</option>
                ))}
              </select>
              <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-foreground-500 pointer-events-none"></i>
            </div>
          </div>
        </div>

        <section className="max-w-7xl mx-auto px-4 md:px-8 pb-20">
          {filtered.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
              {filtered.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="py-20 text-center bg-background-100 rounded-2xl border border-background-200/70">
              <i className="ri-emotion-sad-line text-5xl text-secondary-300"></i>
              <p className="mt-4 text-foreground-700">没有找到匹配的漫画，换个关键词或题材试试吧</p>
              <button
                type="button"
                onClick={() => {
                  setSearchParams(new URLSearchParams());
                  setKeyword("");
                }}
                className="mt-5 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm hover:bg-primary-600"
              >
                清除筛选
              </button>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}